import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  AppState,
  FullProfile,
  OnboardingData,
  ProfileSummary,
  Match,
  Message,
  ChatState,
  TelegramUser,
  TelegramThemeParams,
  TelegramWebApp,
  BasicFilters,
} from '@/types';

interface AppStore extends AppState {
  // User actions
  setUser: (user: FullProfile | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  updateUser: (updates: Partial<FullProfile>) => void;

  // Onboarding actions
  setOnboardingStep: (step: number) => void;
  setOnboardingCompleted: (completed: boolean) => void;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
  resetOnboarding: () => void;

  // Feed actions
  setFeedProfiles: (profiles: ProfileSummary[]) => void;
  addFeedProfiles: (profiles: ProfileSummary[]) => void;
  setCurrentProfileIndex: (index: number) => void;
  setFeedLoading: (loading: boolean) => void;
  setFeedHasMore: (hasMore: boolean) => void;
  removeProfileFromFeed: (profileId: string) => void;
  
  // Feed type actions
  setFeedType: (feedType: 'cards' | 'vertical') => void;
  toggleFeedType: () => void;
  
  // Vertical feed actions
  setVerticalFeedProfiles: (profiles: import('@/types').BrowseCardResponse[]) => void;
  addVerticalFeedProfiles: (profiles: import('@/types').BrowseCardResponse[]) => void;
  updateVerticalFeedProfile: (profileId: number, swipeStatus: 'like' | 'pass' | 'superlike' | null) => void;
  setVerticalFeedLastId: (lastId: number | null) => void;
  setVerticalFeedLoading: (loading: boolean) => void;
  setVerticalFeedHasMore: (hasMore: boolean) => void;

  // Match actions
  setMatches: (matches: Match[]) => void;
  addMatches: (matches: Match[]) => void;
  addMatch: (match: Match) => void;
  updateMatch: (matchId: string, updates: Partial<Match>) => void;
  removeMatch: (matchId: string) => void;
  setMatchesLoading: (loading: boolean) => void;
  setMatchesHasMore: (hasMore: boolean) => void;

  // Chat actions
  setActiveMatch: (matchId: string | null) => void;
  setMessages: (matchId: string, messages: Message[]) => void;
  addMessage: (message: Message) => void;
  addMessages: (matchId: string, messages: Message[]) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  setChatState: (matchId: string, state: Partial<ChatState>) => void;

  // UI actions
  setActiveScreen: (screen: string) => void;
  setModal: (type: string | null, data?: any) => void;
  closeModal: () => void;
  setViewingProfile: (userId: string | null) => void;
  setITQuestParams: (params: { mode: 'onboarding' | 'retest'; specializationId?: number; declaredLevel?: string } | null) => void;
  clearITQuestParams: () => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  hideToast: () => void;

  // Telegram actions
  setTelegramWebApp: (webApp: TelegramWebApp | null) => void;
  setTelegramUser: (user: TelegramUser | null) => void;
  setTelegramTheme: (theme: TelegramThemeParams) => void;
  setTelegramReady: (ready: boolean) => void;

  // Filters actions
  setBasicFilters: (filters: BasicFilters) => void;
  updateBasicFiltersState: (updates: Partial<BasicFilters>) => void;
  resetBasicFiltersState: () => void;
  setHasActiveFilters: (hasActive: boolean) => void;

  // Utility actions
  reset: () => void;
}

const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  onboarding: {
    completed: false,
    currentStep: 0,
    data: {},
  },
  feed: {
    profiles: [],
    currentIndex: 0,
    loading: false,
    hasMore: true,
  },
  feedType: (localStorage.getItem('feedType') as 'cards' | 'vertical') || 'cards',  // Load from localStorage or default to cards
  verticalFeed: {
    profiles: [],
    lastId: null,
    loading: false,
    hasMore: true,
  },
  matches: {
    items: [],
    loading: false,
    hasMore: true,
  },
  chat: {
    activeMatchId: null,
    messages: {},
    chatStates: {},
  },
  ui: {
    activeScreen: 'splash',
    isModalOpen: false,
    modalType: null,
    modalData: null,
    viewingProfileId: null,
    itQuestParams: null,
    toast: {
      visible: false,
      message: '',
      type: 'info',
    },
  },
  telegram: {
    webApp: null,
    user: null,
    theme: {},
    isReady: false,
  },
  filters: {
    basic: null,
    hasActiveFilters: false,
  },
};

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // User actions
        setUser: (user) => set({ user, isAuthenticated: !!user }, false, 'setUser'),
        
        setAuthenticated: (isAuthenticated) => 
          set({ isAuthenticated }, false, 'setAuthenticated'),
        
        updateUser: (updates) => 
          set((state) => ({
            user: state.user ? { ...state.user, ...updates } : null
          }), false, 'updateUser'),

        // Onboarding actions
        setOnboardingStep: (step) =>
          set((state) => ({
            onboarding: { ...state.onboarding, currentStep: step }
          }), false, 'setOnboardingStep'),

        setOnboardingCompleted: (completed) =>
          set((state) => ({
            onboarding: { ...state.onboarding, completed }
          }), false, 'setOnboardingCompleted'),

        updateOnboardingData: (data) =>
          set((state) => ({
            onboarding: {
              ...state.onboarding,
              data: { ...state.onboarding.data, ...data }
            }
          }), false, 'updateOnboardingData'),

        resetOnboarding: () =>
          set((state) => ({
            onboarding: { ...initialState.onboarding }
          }), false, 'resetOnboarding'),

        // Feed actions
        setFeedProfiles: (profiles) =>
          set((state) => ({
            feed: { ...state.feed, profiles, currentIndex: 0 }
          }), false, 'setFeedProfiles'),

        addFeedProfiles: (profiles) =>
          set((state) => ({
            feed: { ...state.feed, profiles: [...state.feed.profiles, ...profiles] }
          }), false, 'addFeedProfiles'),

        setCurrentProfileIndex: (index) =>
          set((state) => ({
            feed: { ...state.feed, currentIndex: index }
          }), false, 'setCurrentProfileIndex'),

        setFeedLoading: (loading) =>
          set((state) => ({
            feed: { ...state.feed, loading }
          }), false, 'setFeedLoading'),

        setFeedHasMore: (hasMore) =>
          set((state) => ({
            feed: { ...state.feed, hasMore }
          }), false, 'setFeedHasMore'),

        removeProfileFromFeed: (profileId) =>
          set((state) => ({
            feed: {
              ...state.feed,
              profiles: state.feed.profiles.filter(p => p.id !== profileId)
            }
          }), false, 'removeProfileFromFeed'),

        // Feed type actions
        setFeedType: (feedType) => {
          // Save to localStorage for persistence
          localStorage.setItem('feedType', feedType);
          set({ feedType }, false, 'setFeedType');
        },

        toggleFeedType: () => {
          const newType = get().feedType === 'cards' ? 'vertical' : 'cards';
          // Save to localStorage for persistence
          localStorage.setItem('feedType', newType);
          set({ feedType: newType }, false, 'toggleFeedType');
        },

        // Vertical feed actions
        setVerticalFeedProfiles: (profiles) =>
          set((state) => ({
            verticalFeed: { ...state.verticalFeed, profiles }
          }), false, 'setVerticalFeedProfiles'),

        addVerticalFeedProfiles: (profiles) =>
          set((state) => ({
            verticalFeed: {
              ...state.verticalFeed,
              profiles: [...state.verticalFeed.profiles, ...profiles]
            }
          }), false, 'addVerticalFeedProfiles'),

        updateVerticalFeedProfile: (profileId, swipeStatus) =>
          set((state) => ({
            verticalFeed: {
              ...state.verticalFeed,
              profiles: state.verticalFeed.profiles.map(p =>
                p.id === profileId ? { ...p, swipe_status: swipeStatus } : p
              )
            }
          }), false, 'updateVerticalFeedProfile'),

        setVerticalFeedLastId: (lastId) =>
          set((state) => ({
            verticalFeed: { ...state.verticalFeed, lastId }
          }), false, 'setVerticalFeedLastId'),

        setVerticalFeedLoading: (loading) =>
          set((state) => ({
            verticalFeed: { ...state.verticalFeed, loading }
          }), false, 'setVerticalFeedLoading'),

        setVerticalFeedHasMore: (hasMore) =>
          set((state) => ({
            verticalFeed: { ...state.verticalFeed, hasMore }
          }), false, 'setVerticalFeedHasMore'),

        // Match actions
        setMatches: (items) =>
          set((state) => ({
            matches: { ...state.matches, items }
          }), false, 'setMatches'),

        addMatches: (newMatches) =>
          set((state) => ({
            matches: { ...state.matches, items: [...state.matches.items, ...newMatches] }
          }), false, 'addMatches'),

        addMatch: (match) =>
          set((state) => ({
            matches: { ...state.matches, items: [match, ...state.matches.items] }
          }), false, 'addMatch'),

        updateMatch: (matchId, updates) =>
          set((state) => ({
            matches: {
              ...state.matches,
              items: state.matches.items.map(match =>
                match.id === matchId ? { ...match, ...updates } : match
              )
            }
          }), false, 'updateMatch'),

        removeMatch: (matchId) =>
          set((state) => ({
            matches: {
              ...state.matches,
              items: state.matches.items.filter(match => match.id !== matchId)
            }
          }), false, 'removeMatch'),

        setMatchesLoading: (loading) =>
          set((state) => ({
            matches: { ...state.matches, loading }
          }), false, 'setMatchesLoading'),

        setMatchesHasMore: (hasMore) =>
          set((state) => ({
            matches: { ...state.matches, hasMore }
          }), false, 'setMatchesHasMore'),

        // Chat actions
        setActiveMatch: (matchId) =>
          set((state) => ({
            chat: { ...state.chat, activeMatchId: matchId }
          }), false, 'setActiveMatch'),

        setMessages: (matchId, messages) =>
          set((state) => ({
            chat: {
              ...state.chat,
              messages: { ...state.chat.messages, [matchId]: messages }
            }
          }), false, 'setMessages'),

        addMessage: (message) =>
          set((state) => {
            const matchMessages = state.chat.messages[message.matchId] || [];
            return {
              chat: {
                ...state.chat,
                messages: {
                  ...state.chat.messages,
                  [message.matchId]: [...matchMessages, message]
                }
              }
            };
          }, false, 'addMessage'),

        addMessages: (matchId, messages) =>
          set((state) => {
            const existingMessages = state.chat.messages[matchId] || [];
            return {
              chat: {
                ...state.chat,
                messages: {
                  ...state.chat.messages,
                  [matchId]: [...existingMessages, ...messages]
                }
              }
            };
          }, false, 'addMessages'),

        updateMessage: (messageId, updates) =>
          set((state) => {
            const updatedMessages = { ...state.chat.messages };
            Object.keys(updatedMessages).forEach(matchId => {
              updatedMessages[matchId] = updatedMessages[matchId].map(message =>
                message.id === messageId ? { ...message, ...updates } : message
              );
            });
            return {
              chat: { ...state.chat, messages: updatedMessages }
            };
          }, false, 'updateMessage'),

        setChatState: (matchId, stateUpdates) =>
          set((state) => ({
            chat: {
              ...state.chat,
              chatStates: {
                ...state.chat.chatStates,
                [matchId]: { ...state.chat.chatStates[matchId], ...stateUpdates }
              }
            }
          }), false, 'setChatState'),

        // UI actions
        setActiveScreen: (screen) =>
          set((state) => ({
            ui: { ...state.ui, activeScreen: screen }
          }), false, 'setActiveScreen'),

        setModal: (modalType, modalData = null) =>
          set((state) => ({
            ui: {
              ...state.ui,
              isModalOpen: !!modalType,
              modalType,
              modalData
            }
          }), false, 'setModal'),

        closeModal: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              isModalOpen: false,
              modalType: null,
              modalData: null
            }
          }), false, 'closeModal'),

        setViewingProfile: (userId) =>
          set((state) => ({
            ui: { ...state.ui, viewingProfileId: userId }
          }), false, 'setViewingProfile'),

        setITQuestParams: (params) =>
          set((state) => ({
            ui: { ...state.ui, itQuestParams: params }
          }), false, 'setITQuestParams'),

        clearITQuestParams: () =>
          set((state) => ({
            ui: { ...state.ui, itQuestParams: null }
          }), false, 'clearITQuestParams'),

        showToast: (message, type = 'info') =>
          set((state) => ({
            ui: {
              ...state.ui,
              toast: { visible: true, message, type }
            }
          }), false, 'showToast'),

        hideToast: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              toast: { ...state.ui.toast, visible: false }
            }
          }), false, 'hideToast'),

        // Telegram actions
        setTelegramWebApp: (webApp) =>
          set((state) => ({
            telegram: { ...state.telegram, webApp }
          }), false, 'setTelegramWebApp'),

        setTelegramUser: (user) =>
          set((state) => ({
            telegram: { ...state.telegram, user }
          }), false, 'setTelegramUser'),

        setTelegramTheme: (theme) =>
          set((state) => ({
            telegram: { ...state.telegram, theme }
          }), false, 'setTelegramTheme'),

        setTelegramReady: (isReady) =>
          set((state) => ({
            telegram: { ...state.telegram, isReady }
          }), false, 'setTelegramReady'),

        // Filters actions
        setBasicFilters: (filters) =>
          set((state) => ({
            filters: { ...state.filters, basic: filters }
          }), false, 'setBasicFilters'),

        updateBasicFiltersState: (updates) =>
          set((state) => ({
            filters: {
              ...state.filters,
              basic: state.filters.basic ? { ...state.filters.basic, ...updates } : null
            }
          }), false, 'updateBasicFiltersState'),

        resetBasicFiltersState: () =>
          set((state) => ({
            filters: { ...state.filters, basic: null, hasActiveFilters: false }
          }), false, 'resetBasicFiltersState'),

        setHasActiveFilters: (hasActive) =>
          set((state) => ({
            filters: { ...state.filters, hasActiveFilters: hasActive }
          }), false, 'setHasActiveFilters'),

        // Utility actions
        reset: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'itinder-store',
        partialize: (state) => ({
          // Only persist essential data
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          onboarding: state.onboarding,
        }),
      }
    ),
    {
      name: 'ITinder Store',
    }
  )
);

// Utility hooks for specific store slices
export const useUser = () => useAppStore((state) => state.user);
export const useAuth = () => useAppStore((state) => ({
  user: state.user,
  isAuthenticated: state.isAuthenticated,
  setUser: state.setUser,
  setAuthenticated: state.setAuthenticated,
}));

export const useOnboarding = () => useAppStore((state) => ({
  ...state.onboarding,
  setOnboardingStep: state.setOnboardingStep,
  setOnboardingCompleted: state.setOnboardingCompleted,
  updateOnboardingData: state.updateOnboardingData,
  resetOnboarding: state.resetOnboarding,
}));

export const useFeed = () => useAppStore((state) => ({
  ...state.feed,
  setFeedProfiles: state.setFeedProfiles,
  addFeedProfiles: state.addFeedProfiles,
  setCurrentProfileIndex: state.setCurrentProfileIndex,
  setFeedLoading: state.setFeedLoading,
  setFeedHasMore: state.setFeedHasMore,
  removeProfileFromFeed: state.removeProfileFromFeed,
}));

export const useMatches = () => useAppStore((state) => ({
  ...state.matches,
  setMatches: state.setMatches,
  addMatches: state.addMatches,
  addMatch: state.addMatch,
  updateMatch: state.updateMatch,
  removeMatch: state.removeMatch,
  setMatchesLoading: state.setMatchesLoading,
  setMatchesHasMore: state.setMatchesHasMore,
}));

export const useChat = () => useAppStore((state) => ({
  ...state.chat,
  setActiveMatch: state.setActiveMatch,
  setMessages: state.setMessages,
  addMessage: state.addMessage,
  addMessages: state.addMessages,
  updateMessage: state.updateMessage,
  setChatState: state.setChatState,
}));

export const useUI = () => useAppStore((state) => ({
  ...state.ui,
  setActiveScreen: state.setActiveScreen,
  setModal: state.setModal,
  closeModal: state.closeModal,
  showToast: state.showToast,
  hideToast: state.hideToast,
}));

export const useTelegram = () => useAppStore((state) => ({
  ...state.telegram,
  setTelegramWebApp: state.setTelegramWebApp,
  setTelegramUser: state.setTelegramUser,
  setTelegramTheme: state.setTelegramTheme,
  setTelegramReady: state.setTelegramReady,
}));

export const useFilters = () => useAppStore((state) => ({
  ...state.filters,
  setBasicFilters: state.setBasicFilters,
  updateBasicFiltersState: state.updateBasicFiltersState,
  resetBasicFiltersState: state.resetBasicFiltersState,
  setHasActiveFilters: state.setHasActiveFilters,
}));
